// VideoSpotlight.jsx — pieza dinámica de la campaña en video.

import React from 'react'
import { LogoCbqImg } from '../CampaignMediaImg.jsx'

function VideoSpotlight() {
  const ref = React.useRef(null);
  const [playing, setPlaying] = React.useState(true);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Forzar silencio absoluto pase lo que pase
    v.muted = true;
    v.volume = 0;
    const reMute = () => { v.muted = true; v.volume = 0; };
    v.addEventListener("volumechange", reMute);
    v.addEventListener("play", reMute);
    v.addEventListener("loadeddata", reMute);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => {
      io.disconnect();
      v.removeEventListener("volumechange", reMute);
      v.removeEventListener("play", reMute);
      v.removeEventListener("loadeddata", reMute);
    };
  }, []);

  function togglePlay() {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  }

  return (
    <section id="video" style={{ paddingTop: 64, paddingBottom: 96 }}>
      <div className="wrap">
        <div className="section-head fade-up" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24 }}>
          <div>
            <div className="eyebrow">Contigo llegamos más alto</div>
            <h2 className="h-section" style={{ marginTop: 14 }}>
              Cada aporte, <em>un paso</em> más cerca
            </h2>
          </div>
        </div>

        <div
          className="fade-up"
          style={{
            position: "relative",
            borderRadius: 24,
            overflow: "hidden",
            border: "1px solid var(--line-2)",
            background: "#000",
            boxShadow: "0 30px 80px -30px rgba(0,0,0,.6)",
            aspectRatio: "16 / 9",
          }}
        >
          <video
            ref={ref}
            src="/campaign-media/campaign-video-silent.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disableRemotePlayback
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />

          {/* Cover del sello generación IA — gradiente negro en toda la franja inferior */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 0, right: 0, bottom: 0,
              height: "45%",
              background:
                "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.55) 35%, rgba(0,0,0,.95) 75%, rgba(0,0,0,1) 100%)",
              pointerEvents: "none",
            }}
          />

          {/* Gradiente sutil para legibilidad de overlay */}
          <div
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,.55) 100%)",
              pointerEvents: "none",
            }}
          />

          {/* Tapa el sello de generación IA (esquina inferior derecha del video) con el logo del Cuerpo de Bomberos */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              right: "3%",
              bottom: "9%",
              width: "8%",
              aspectRatio: "1 / 1",
              borderRadius: 8,
              background: "#0b0a08",
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
            }}
          >
            <LogoCbqImg
              alt=""
              style={{ width: "72%", height: "72%", objectFit: "contain" }}
            />
          </div>

          {/* Overlay con título de campaña */}
          <div
            style={{
              position: "absolute",
              left: 28, bottom: 24, right: 28,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 16,
              pointerEvents: "none",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: "rgba(11,10,8,.6)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  color: "#fff",
                  textTransform: "uppercase",
                  marginBottom: 14,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--gold)" }} />
                #MecanicaParaQuilpue
              </div>
              <div
                className="video-spotlight-title"
                style={{
                  fontFamily: "var(--display)",
                  fontSize: 36,
                  lineHeight: 1,
                  textTransform: "uppercase",
                  color: "#fff",
                  letterSpacing: 0,
                  textShadow: "0 2px 16px rgba(0,0,0,.5)",
                }}
              >
                Con tu aporte <em style={{ fontFamily: "var(--script)", color: "var(--gold)", textTransform: "none", fontSize: 32 }}>llegaremos</em> más alto
              </div>
            </div>

            {/* Controles */}
            <div style={{ display: "flex", gap: 10, pointerEvents: "auto" }}>
              <button
                onClick={togglePlay}
                aria-label={playing ? "Pausar video" : "Reproducir video"}
                style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: "rgba(11,10,8,.65)",
                  border: "1px solid rgba(255,255,255,.2)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  cursor: "pointer",
                  display: "grid", placeItems: "center",
                  color: "#fff",
                }}
              >
                {playing ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <rect x="3" y="2" width="3" height="10" rx="0.5" />
                    <rect x="8" y="2" width="3" height="10" rx="0.5" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <path d="M 3 2 L 12 7 L 3 12 Z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export { VideoSpotlight };
